import type { MutableRefObject } from 'react';

import { useEffect } from 'react';

export interface Callbacks {
  onUp?: (event: KeyboardEvent) => void;
  onDown?: (event: KeyboardEvent) => void;
  onLeft?: (event: KeyboardEvent) => void;
  onRight?: (event: KeyboardEvent) => void;
  onDelete?: (event: KeyboardEvent) => void;
}

export const SupportedKeys = {
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
  Home: 'Home',
  End: 'End',
  Delete: 'Delete',
} as const;

/**
 * Attaches keyboard controls to a DOM element.
 * @param {MutableRefObject<HTMLElement | null>} [elementRef] A React ref to a DOM element that will get keyboard listeners attached.
 * @default undefined
 * @param {'horizontal' | 'vertical'} [direction] The direction of the element.
 * @default 'horizontal'
 * @param {Callbacks} [callbacks] An object containing callbacks to be called when the user presses a key.
 * @default {}
 */
export function useKeyboardControls(
  elementRef?: MutableRefObject<HTMLElement | null>,
  { onUp = () => {}, onDown = () => {}, onLeft = () => {}, onRight = () => {}, onDelete = () => {} } = {} as Callbacks,
  direction: 'horizontal' | 'vertical' = 'horizontal'
) {
  useEffect(() => {
    const element = elementRef?.current;

    if (!element) return;

    const keydownEventListener = (event: KeyboardEvent) => {
      if (event.key in SupportedKeys === false) return;

      event.preventDefault();

      switch (event.key) {
        case SupportedKeys.ArrowUp:
          onUp(event);
          break;
        case SupportedKeys.ArrowDown:
          onDown(event);
          break;
        case SupportedKeys.ArrowLeft:
          onLeft(event);
          break;
        case SupportedKeys.ArrowRight:
          onRight(event);
          break;
        case SupportedKeys.Home:
          if (direction === 'horizontal') onUp(event);
          if (direction === 'vertical') onLeft(event);
          break;
        case SupportedKeys.End:
          if (direction === 'horizontal') onDown(event);
          if (direction === 'vertical') onRight(event);
          break;
        case SupportedKeys.Delete:
          onDelete(event);
      }
    };

    element.addEventListener('keydown', keydownEventListener);

    return () => {
      element.removeEventListener('keydown', keydownEventListener);
    };
  }, [elementRef, direction, onUp, onDown, onLeft, onRight, onDelete]);
}
